import { useState, useRef, useCallback, useEffect } from 'react';
import { getCampaignId, getToken } from '../api/client.js';

const MAX_SIZE = 500 * 1024; // 500 Ko
const QUALITY_STEPS = [0.7, 0.5, 0.3];

/**
 * Compress an image blob via canvas to stay under MAX_SIZE.
 * @param {string} base64 - Original base64 image data
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @returns {Promise<Blob>} Compressed JPEG blob
 */
async function compressImage(base64, width, height) {
  const canvas = document.createElement('canvas');

  // Scale down if very large
  let w = width;
  let h = height;
  const maxDim = 1280;
  if (w > maxDim || h > maxDim) {
    const ratio = Math.min(maxDim / w, maxDim / h);
    w = Math.round(w * ratio);
    h = Math.round(h * ratio);
  }

  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');

  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = `data:image/jpeg;base64,${base64}`;
  });
  ctx.drawImage(img, 0, 0, w, h);

  for (const quality of QUALITY_STEPS) {
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (blob && blob.size <= MAX_SIZE) return blob;
  }

  // Last resort: lowest quality
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.2));
}

/**
 * Photo capture component with camera, preview, and upload.
 * @param {{ onPhotoCaptured: (url: string) => void, onSkip: () => void }} props
 */
export default function PhotoCapture({ onPhotoCaptured, onSkip }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [streaming, setStreaming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);

  const startCamera = useCallback(async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setStreaming(true);
      }
    } catch (_err) {
      setError("Impossible d'accéder à la caméra");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  }, []);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
    setPreview({ base64, width: video.videoWidth, height: video.videoHeight });
    stopCamera();
  }, [stopCamera]);

  const handleKeep = useCallback(async () => {
    if (!preview) return;
    setUploading(true);
    setError('');
    try {
      const blob = await compressImage(preview.base64, preview.width, preview.height);
      const form = new FormData();
      form.append('photo', blob, 'proof.jpg');
      form.append('type', 'proof');

      // Attach GPS coordinates if available
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        form.append('gpsLat', String(pos.coords.latitude));
        form.append('gpsLng', String(pos.coords.longitude));
      } catch (_geoErr) {
        // GPS not available — continue without
      }

      const campaignId = getCampaignId();
      const response = await fetch(`/api/c/${campaignId}/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'X-Campaign-Id': campaignId,
        },
        body: form,
      });

      if (!response.ok) throw new Error('Erreur upload');
      const data = await response.json();
      onPhotoCaptured(data.url);
    } catch (_uploadErr) {
      setError('Échec upload. Réessayez.');
    } finally {
      setUploading(false);
    }
  }, [preview, onPhotoCaptured]);

  const handleRetake = useCallback(() => {
    setPreview(null);
    startCamera();
  }, [startCamera]);

  return (
    <div className="bg-white rounded-xl p-4 space-y-3">
      <h3 className="font-bold text-lg">Photo de livraison</h3>
      <p className="text-sm text-[#64748b]">Prenez en photo les vélos livrés chez le client</p>

      {!preview && (
        <div className="relative">
          <video ref={videoRef} className="w-full rounded-lg bg-black" playsInline muted />
          {streaming && (
            <button
              onClick={capture}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-full w-14 h-14 border-4 border-blue-600 shadow-lg"
              aria-label="Capturer"
            />
          )}
          {!streaming && (
            <button onClick={startCamera} className="w-full bg-blue-600 text-white rounded-xl py-3 font-medium">
              Ouvrir la caméra
            </button>
          )}
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />

      {preview && (
        <div className="space-y-3">
          <img src={`data:image/jpeg;base64,${preview.base64}`} alt="Preview" className="w-full rounded-lg" />
          <div className="flex gap-2">
            <button
              onClick={handleKeep}
              disabled={uploading}
              className="flex-1 bg-green-600 text-white rounded-xl py-3 font-medium disabled:opacity-50"
            >
              {uploading ? 'Envoi...' : 'Garder'}
            </button>
            <button
              onClick={handleRetake}
              disabled={uploading}
              className="flex-1 border border-gray-300 rounded-xl py-3 font-medium"
            >
              Reprendre
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-red-600 text-sm text-center">{error}</p>}

      <button
        onClick={() => setShowSkipConfirm(true)}
        className="w-full text-gray-500 text-sm underline py-2"
      >
        Passer sans photo
      </button>

      {showSkipConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full text-center space-y-3">
            <p className="font-bold text-[#0f172a]">Passer sans photo ?</p>
            <p className="text-sm text-[#64748b]">La photo sert de preuve de livraison.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowSkipConfirm(false)} className="flex-1 border border-gray-300 rounded-xl py-3 font-medium">Prendre la photo</button>
              <button onClick={() => { setShowSkipConfirm(false); onSkip(); }} className="flex-1 bg-gray-500 text-white rounded-xl py-3 font-medium">Passer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
